/**
 * Spec §14.2 page 7 + §14.3: audio cards must say NO RECORDING / LOCAL MIC ONLY / AUTHORIZED IMPORT /
 * AUDIO SILENCED — never a vague green check. Recording stays OFF unless the backend feature flag is on
 * AND a consent ledger entry passes every gate.
 */
import { useState } from "react";

import {
  useAudioSessions, useConsents, useCreateConsent, useDeleteAudioSession, useExportAudio, useHealth,
  useRevokeConsent, useSignedPlayback,
} from "../api/hooks.js";
import { Empty, PageState } from "../components/PageState.js";

const SOURCES = ["OWN_APP_MIC_OPT_IN", "FOREGROUND_MIC_EXPERIMENTAL", "IMPORTED_LAWFUL_AUDIO"] as const;

export function AudioConsentPage({ deviceId }: { deviceId: string }) {
  const consents = useConsents(deviceId);
  const sessions = useAudioSessions(deviceId);
  const health = useHealth();
  const create = useCreateConsent(deviceId);
  const revoke = useRevokeConsent();
  const del = useDeleteAudioSession();
  const exportAudio = useExportAudio();
  const playback = useSignedPlayback();
  const [source, setSource] = useState<string>(SOURCES[0]!);
  const [days, setDays] = useState("7");
  const [justification, setJustification] = useState("safety review");
  const [note, setNote] = useState("");

  if (!deviceId) return <Empty label="No device selected." />;
  const captureEnabled = health.data?.audio_capture_enabled === true;
  const active = (consents.data ?? []).filter((c) => !c.revoked_at);

  return (
    <PageState query={{ isPending: consents.isPending, error: consents.error }}>
      <div className="grid">
        <div className="card">
          <h3>Capture switch</h3>
          <div className="value">{captureEnabled ? "ON" : "OFF"}</div>
          <div className="hint">
            {captureEnabled ? "backend feature flag enabled" : "feature flag off — recording is not permitted"}
          </div>
        </div>
        <div className="card">
          <h3>Consent state</h3>
          <div className="value">{active.length > 0 ? "CONSENT ON FILE" : "NO CONSENT"}</div>
          <div className="hint">{active.length} valid ledger row(s)</div>
        </div>
        <div className="card">
          <h3>Latest session</h3>
          <div className="value">{audioCardLabel(sessions.data?.[0])}</div>
          <div className="hint">completeness {fmtCompleteness(sessions.data?.[0]?.completeness)}</div>
        </div>
      </div>

      {captureEnabled && active.length === 0 ? (
        <p className="notice warn">Capture is enabled but no lawful consent exists — sessions stay at NO RECORDING.</p>
      ) : null}

      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          const now = new Date();
          create.mutate({
            scope: { participants: ["child"], child_disclosure: true },
            capture_source: source,
            participants_and_proof: { child: { consented: true } },
            jurisdictions: { "US-FL": { basis: "all_party_consent" } },
            valid_from: now.toISOString(),
            valid_to: new Date(now.getTime() + Number(days) * 86_400_000).toISOString(),
            notice_state: "ACKNOWLEDGED",
          });
        }}
      >
        <h3>Record authorization</h3>
        <label htmlFor="src">Capture source</label>
        <select id="src" value={source} onChange={(e) => setSource(e.target.value)}>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label htmlFor="days">Valid for (days)</label>
        <input id="days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)} />
        <button disabled={create.isPending}>Grant consent</button>
        {create.error ? <p className="notice danger">{create.error.message}</p> : null}
      </form>

      <section>
        <h3>Consent ledger</h3>
        {(consents.data ?? []).length === 0 ? (
          <p className="notice">No consent records.</p>
        ) : (
          <table>
            <thead><tr><th>Source</th><th>Notice</th><th>Revoked</th><th>Action</th></tr></thead>
            <tbody>
              {(consents.data ?? []).map((c) => (
                <tr key={c.consent_id}>
                  <td className="mono">{c.capture_source}</td>
                  <td>{c.notice_state}</td>
                  <td>{c.revoked_at ? new Date(c.revoked_at).toLocaleString() : "active"}</td>
                  <td>
                    <button disabled={!c.revoked_at || revoke.isPending} onClick={() => revoke.mutate(c.consent_id)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3>Session audio</h3>
        {(sessions.data ?? []).length === 0 ? (
          <p className="notice">NO RECORDING — no audio sessions uploaded.</p>
        ) : (
          <table>
            <thead><tr><th>Started</th><th>State</th><th>Completeness</th><th>Playback</th><th>Export</th><th>Delete</th></tr></thead>
            <tbody>
              {(sessions.data ?? []).map((s) => (
                <tr key={s.id}>
                  <td>{new Date(s.started_at).toLocaleString()}</td>
                  <td>{audioCardLabel(s)}</td>
                  <td>{fmtCompleteness(s.completeness)}</td>
                  <td>
                    <button disabled={playback.isPending} onClick={() => playback.mutate(s.id)}>Signed link</button>
                  </td>
                  <td>
                    <button disabled={exportAudio.isPending} onClick={() => exportAudio.mutate({ sessionId: s.id, justification })}>
                      Export
                    </button>
                  </td>
                  <td>
                    <button disabled={del.isPending} onClick={() => del.mutate(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {playback.data ? <p className="notice mono">playback bundle expires {new Date(playback.data.expires_at * 1000).toLocaleTimeString()}</p> : null}
        {exportAudio.error ? <p className="notice danger">export blocked: {exportAudio.error.message}</p> : null}
        <label htmlFor="just">Export justification (logged in data-access audit)</label>
        <input id="just" value={justification} onChange={(e) => setJustification(e.target.value)} />
      </section>
      <label htmlFor="note">Retention note</label>
      <textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="local retention days follow server defaults" />
      <p className="muted">
        Raw audio and detailed transcripts are privileged-parent gated: export requires MFA step-up and every
        access is written to the data-access log.
      </p>
    </PageState>
  );
}

type SessionRow = { capture_state?: string; completeness?: number | null; failure_reason?: string | null };

function audioCardLabel(row: SessionRow | undefined): string {
  if (!row) return "NO RECORDING";
  if (row.failure_reason) return `UNAVAILABLE (${row.failure_reason})`;
  switch (row.capture_state) {
    case "OS_SILENCED":
      return "AUDIO SILENCED";
    case "RECORDING":
      return "LOCAL MIC ONLY";
    case "SYNCED":
      return "UPLOADED";
    case "PENDING_UPLOAD":
      return "PARTIAL UPLOAD";
    default:
      return row.capture_state ?? "NO RECORDING";
  }
}

function fmtCompleteness(v: number | null | undefined): string {
  return v === null || v === undefined ? "not measured" : `${Math.round(v * 100)}%`;
}
